import { useEffect } from 'react';
import type { Event, StreamChat } from 'stream-chat';
import type { StreamChatGenerics } from '../types';
import { DefaultStreamChatGenerics } from 'stream-chat-react';

/**
 * Creates a function that sends messages to parent window via postMessage
 * PARAMETERS:
 * - parent: Target origin URL for security (only this origin can receive messages)
 * RETURNS:
 * Function that sends message to parent window
 * SECURITY:
 * Uses targetOrigin parameter to prevent messages being sent to malicious sites
 */
const notifyParent = (parent: string) => (message: any) => {
  window.parent.postMessage(message, parent);
};

/**
 * CONSTANTS: YouTube tutorial video identifiers
 * YOUTUBE_LINK: Expected YouTube link for tutorial
 * YOUTUBE_LINK_RE: Regex to match YouTube link variations
 */
const YOUTUBE_LINK = 'https://youtu.be/Ujvy-DEA-UM';
const YOUTUBE_LINK_RE = /^(http(s)?:\/\/)?(youtu.be\/Ujvy-DEA-UM\/?)$/;

/**
 * CHECKLIST ITEM IDENTIFIERS
 * These strings match the checklist items on the parent website
 * Each represents a tutorial task the user needs to complete
 * IMPORTANT: Keep synchronized with website's checklist implementation
 */
const [REACT_TO_MESSAGE, RUN_GIPHY, SEND_YOUTUBE, DRAG_DROP, START_THREAD, SEND_MESSAGE] = [
  'react-to-message',
  'run-giphy',
  'send-youtube',
  'drag-drop-image',
  'start-thread',
  'send-message',
];

export const useChecklist = (chatClient: StreamChat | null, targetOrigin: string) => {
  useEffect(() => {
    // Create notify function bound to target origin
    const notify = notifyParent(targetOrigin);

    /**
     * EVENT HANDLER: handleNewEvent
     * Processes Stream Chat events and notifies parent when checklist actions occur
     * LOGIC:
     * 1. Extract message and event type from event props
     * 2. Switch on event type:
     *    - reaction.new → User added reaction to message
     *    - message.new → Check message content for specific actions
     * 3. For message.new, check in priority order:
     *    - Has /giphy command → notify RUN_GIPHY
     *    - Has YouTube attachment → notify SEND_YOUTUBE
     *    - Has image attachment → notify DRAG_DROP
     *    - Has parent_id (is reply) → notify START_THREAD
     *    - Default → notify SEND_MESSAGE
     */
    const handleNewEvent = (props: Event<StreamChatGenerics>) => {
      const { message, type } = props;

      switch (type) {
        // User reacted to a message (added emoji reaction)
        case 'reaction.new':
          notify(REACT_TO_MESSAGE);
          break;
        
        // New message sent - check for specific actions
        case 'message.new':
          // Priority 1: Check for /giphy command
          if (message && message.command === 'giphy') {
            notify(RUN_GIPHY);
            break;
          }
          
          // Priority 2: Check for attachments
          if (message && message.attachments && message.attachments.length) {
            const [attachment] = message.attachments;
            
            // Check for YouTube video attachment (tutorial link)
            if (
              attachment.type === 'video' &&
              (attachment.og_scrape_url === YOUTUBE_LINK ||
                YOUTUBE_LINK_RE.test(attachment.og_scrape_url as string))
            ) {
              notify(SEND_YOUTUBE);
              break;
            }
            
            // Check for image attachment (drag and drop)
            if (attachment.type === 'image') {
              notify(DRAG_DROP);
              break;
            }
          }
          
          // Priority 3: Check if message is a thread reply
          if (message && message.parent_id) {
            notify(START_THREAD);
            break;
          }
          
          // Default: Just sending a regular message
          notify(SEND_MESSAGE);
          break;
        
        default:
          break;
      }
    };

    /**
     * SETUP: Attach event listener to chat client
     * 
     * Only attaches if chatClient exists (user is connected)
     */
    if (chatClient) {
      chatClient.on(handleNewEvent);
    }

    /**
     * CLEANUP: Remove event listener on unmount
     * 
     * Prevents memory leaks and duplicate event handlers
     * Uses optional chaining in case chatClient is null
     */
    return () => chatClient?.off(handleNewEvent);
  }, [chatClient, targetOrigin]);
};
